/**
 * Subscription Trends Chart Component
 * Line chart showing subscription status trends over time
 */
import React from 'react';
import { Card } from 'antd';
import { Line } from '@ant-design/plots';
import { SubscriptionTrendData } from '../../services/superAdminService';
import './SubscriptionTrendsChart.css';

interface SubscriptionTrendsChartProps {
  data?: SubscriptionTrendData[];
  loading?: boolean;
}

const SubscriptionTrendsChart: React.FC<SubscriptionTrendsChartProps> = ({ data, loading = false }) => {
  if (loading || !data || data.length === 0) {
    return (
      <Card className="subscription-trends-chart-card" loading={true}>
        <div className="chart-title">Subscription Trends</div>
      </Card>
    );
  }

  // Transform data for the chart
  const chartData = data.flatMap((d) => [
    { month: d.month, type: 'Active Subscriptions', value: d.active_subscriptions },
    { month: d.month, type: 'Expired Subscriptions', value: d.expired },
    { month: d.month, type: 'Expiring Soon', value: d.expiring_soon },
  ]);

  const config = {
    data: chartData,
    xField: 'month',
    yField: 'value',
    seriesField: 'type',
    smooth: true,
    point: {
      size: 5,
      shape: 'circle',
      style: {
        fill: 'white',
        stroke: '#5B8FF9',
        lineWidth: 2,
      },
    },
    legend: {
      position: 'bottom' as const,
      itemName: {
        formatter: (text: string) => {
          return text;
        },
      },
    },
    color: ['#4caf50', '#f44336', '#ff9800'],
    xAxis: {
      label: {
        style: {
          fill: '#666',
          fontSize: 12,
        },
      },
      grid: {
        line: {
          style: {
            stroke: '#e0e0e0',
            lineDash: [4, 4],
          },
        },
      },
    },
    yAxis: {
      label: {
        style: {
          fill: '#666',
          fontSize: 12,
        },
      },
      grid: {
        line: {
          style: {
            stroke: '#e0e0e0',
            lineDash: [4, 4],
          },
        },
      },
    },
    tooltip: {
      customContent: (title: string, items: any[]) => {
        if (!items || items.length === 0) return '';
        return `
          <div style="padding: 12px; background: white; border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
            <div style="font-weight: 600; margin-bottom: 10px; color: #333; font-size: 14px;">${title}</div>
            ${items.map((item: any) => `
              <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 6px;">
                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${item.color};"></span>
                <span style="color: #666; font-size: 13px;">${item.name}:</span>
                <span style="color: ${item.color}; font-weight: 600; font-size: 13px;">${item.value}</span>
              </div>
            `).join('')}
          </div>
        `;
      },
    },
    animation: {
      appear: {
        animation: 'wave-in',
        duration: 2000,
      },
    },
  };

  return (
    <Card className="subscription-trends-chart-card">
      <div className="chart-title">Subscription Trends</div>
      <div className="chart-subtitle">Track subscription status over time</div>
      <Line {...config} />
    </Card>
  );
};

export default SubscriptionTrendsChart;
